import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { findAll, clearAuthCache } from '../../services/Service';
import {
  onLogin,
  setAuth,
  setRoles,
  setPermisos,
  setLinkPermisos,
} from '../Auth/UserLoginReducer';

interface AuthContextType {
  isAuthenticated: boolean | null;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<
    boolean | null
  >(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      console.log('=== FRONTEND DEBUG ===');
      console.log('Checking auth status...');

      // Limpiar cache antes de verificar para asegurar datos frescos
      clearAuthCache();

      const statusResponse = await findAll('auth/status');
      console.log('Status Response Status:', statusResponse?.status);
      console.log('Status Response Data:', statusResponse?.data);

      if (statusResponse?.data?.authenticated) {
        console.log('Usuario autenticado, cargando datos...');

        const [authoritiesResponse, userResponse] = await Promise.all(
          [findAll('auth/authorities'), findAll('usuarios/username')]
        );

        if (authoritiesResponse?.data?.authorities) {
          const rolesRaw = authoritiesResponse.data.authorities;
          console.log('Raw authorities:', rolesRaw);

          try {
            let parsedRoles;
            if (typeof rolesRaw === 'string') {
              parsedRoles = JSON.parse(rolesRaw);
            } else {
              parsedRoles = rolesRaw;
            }

            if (
              Array.isArray(parsedRoles) &&
              parsedRoles.length > 0
            ) {
              dispatch(setRoles(true));
              const nombres: string[] = [];
              const links: string[] = [];

              let auth;
              if (typeof parsedRoles[0]?.authority === 'string') {
                auth = JSON.parse(parsedRoles[0].authority);
              } else {
                auth = parsedRoles[0]?.authority || {};
              }

              if (auth?.menu && Array.isArray(auth.menu)) {
                auth.menu.forEach((rol: any) => {
                  if (rol.name) nombres.push(rol.name);
                  if (rol.sub && Array.isArray(rol.sub)) {
                    rol.sub.forEach((rolSub: any) => {
                      if (rolSub.acro) nombres.push(rolSub.acro);
                      if (rolSub.link) links.push(rolSub.link);
                    });
                  }
                });
              }
              dispatch(setPermisos(nombres));
              dispatch(setLinkPermisos(links));
            }
          } catch (parseError) {
            console.error('Error parsing authorities:', parseError);
          }
        }

        if (userResponse?.data) {
          dispatch(onLogin(userResponse.data));

          // Mantener compatibilidad con localStorage
          localStorage.setItem(
            'login',
            JSON.stringify({
              isAuth: true,
              usuario: userResponse.data,
            })
          );
        }

        dispatch(setAuth(true));
        setIsAuthenticated(true);
      } else {
        console.log('Usuario no autenticado');
        setIsAuthenticated(false);
      }
    } catch (error: any) {
      console.error('Error in checkAuth:', error);
      setIsAuthenticated(false);
    } finally {
      setIsInitialized(true);
    }
  }, [dispatch]);

  useEffect(() => {
    const shouldCheckAuth = () => {
      const publicRoutes = [
        '/login',
        '/',
        '/register',
        '/forgot-password',
      ];
      const isPublicRoute = publicRoutes.includes(location.pathname);

      console.log('Current path:', location.pathname);
      console.log('Is public route:', isPublicRoute);

      // Siempre verificar autenticación, excepto en rutas públicas
      return !isPublicRoute;
    };

    if (shouldCheckAuth()) {
      console.log('Checking auth on mount...');
      checkAuth();
    } else {
      console.log('Skipping auth check - public route');
      setIsAuthenticated(false);
      setIsInitialized(true);
    }
  }, [location.pathname, checkAuth]);

  // No renderizar hijos hasta que la verificación esté completa
  if (!isInitialized) {
    return <div>Verificando autenticación...</div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuthContext must be used within an AuthProvider'
    );
  }
  return context;
};
